import LegalLayout from "../components/LegalLayout";

export default function RefundPolicy() {
  return (
    <LegalLayout
      title="Refund & Returns Policy"
      lastUpdated="Effective Date: May 2, 2026 · Last Updated: May 2, 2026"
    >
      <h2><span className="doc-section-num">1.</span>Overview</h2>
      <p>
        At eGeez Inc.&trade; we are committed to ensuring every fan has a positive experience on our
        Platform. If you are not satisfied with a purchase made through the eGeez marketplace
        application, we are here to help.
      </p>
      <p>
        This policy applies to all purchases made through the eGeez app and Platform. Please read
        it carefully as return eligibility, timelines, and outcomes may vary depending on the
        licensee or seller.
      </p>

      <h2><span className="doc-section-num">2.</span>Return Eligibility</h2>
      <p>
        You may request a return within <strong>15 days from the date of purchase</strong> as
        confirmed in your order on the eGeez app.
      </p>
      <p>To be eligible for a return:</p>
      <ul>
        <li>The return request must be initiated within 15 days of purchase date</li>
        <li>The item must be unused, in its original condition, and in original packaging where applicable</li>
        <li>Proof of purchase must be available through your Order History on the app</li>
      </ul>
      <p><strong>The following are non-refundable under any circumstances:</strong></p>
      <ul>
        <li>Shipping fees</li>
        <li>Items marked as <strong>Final Sale</strong> at the time of purchase</li>
        <li>Gift cards and wallet credits except where required by applicable law</li>
        <li>Premiere Drop pre-orders cancelled after 48 hours of placement</li>
      </ul>

      <h2><span className="doc-section-num">3.</span>Third-Party Licensee Return Policies</h2>
      <p>
        eGeez operates as a marketplace platform connecting fans directly with verified licensees.
        Please note:
      </p>
      <ul>
        <li>Some licensees on the Platform may have their own return policies which may differ from this general policy</li>
        <li>Where a licensee does not accept returns, this will be <strong>clearly disclosed on the product page and at checkout</strong> before you complete your purchase</li>
        <li>eGeez strongly encourages all licensees to accept returns and works to ensure fair treatment of all customers</li>
      </ul>

      <h2><span className="doc-section-num">4.</span>How To Initiate a Return</h2>
      <h3>Step 1 — Open Order History</h3>
      <p>
        Go to your eGeez app → Account → Order History → Select the relevant order → Tap{" "}
        <strong>Return</strong>.
      </p>
      <h3>Step 2 — Contact Our Happiness Centre</h3>
      <p>
        Alternatively go to Account Settings → Happiness Centre to connect with our team via chat.
        A Happiness Agent will guide you through the next steps.
      </p>
      <h3>Step 3 — Submit Required Information</h3>
      <p>You will be prompted to provide:</p>
      <ul>
        <li>Clear photos of the item received</li>
        <li>Description of the issue — wrong item, damaged, quality concern, not as described</li>
        <li>Original packaging photos where applicable</li>
      </ul>
      <h3>Step 4 — Return Label</h3>
      <p>
        Once your return is reviewed and approved a return shipping label will be generated and
        sent to you. Please print and attach this label before sending your item back.
      </p>
      <h3>Step 5 — Licensee Review</h3>
      <p>
        The relevant licensee will review your return request based on submitted information and
        photos. Additional photos may be requested for further clarification.
      </p>

      <h2><span className="doc-section-num">5.</span>Return Outcomes</h2>
      <p>Following licensee review, one of the following outcomes will apply:</p>
      <table>
        <thead>
          <tr>
            <th>Outcome</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td data-label="Outcome"><strong>Full Refund</strong></td>
            <td data-label="Details">Item is confirmed as damaged, incorrect, or not as described</td>
          </tr>
          <tr>
            <td data-label="Outcome"><strong>Partial Refund</strong></td>
            <td data-label="Details">Item has minor issues or partial fault is determined</td>
          </tr>
          <tr>
            <td data-label="Outcome"><strong>No Refund</strong></td>
            <td data-label="Details">Item is confirmed in good condition as described</td>
          </tr>
          <tr>
            <td data-label="Outcome"><strong>GEMS Compensation</strong></td>
            <td data-label="Details">Bonus points or wallet credits may be offered as goodwill in certain cases</td>
          </tr>
        </tbody>
      </table>

      <h2><span className="doc-section-num">6.</span>Refund Processing</h2>
      <ul>
        <li>Approved refunds are processed within <strong>3 to 5 business days</strong> of approval</li>
        <li>Refunds are issued to the <strong>original payment method only</strong> — no exceptions</li>
        <li>Refunds will not be processed until the return process is fully completed and the return ticket is closed</li>
        <li>eGeez is not responsible for delays caused by licensee review timelines or incomplete return submissions by the customer</li>
      </ul>

      <h2><span className="doc-section-num">7.</span>Exchanges</h2>
      <p>
        eGeez does not currently offer exchanges or store credit. If you are unhappy with your
        purchase please follow the return process outlined in Section 4.
      </p>

      <h2><span className="doc-section-num">8.</span>Pop-Up Shop and In-Person Purchases</h2>
      <p>
        For items purchased from pop-up shops or physical retail events operated by licensees or
        third-party vendors:
      </p>
      <ul>
        <li>Do not ship items back unless the vendor&rsquo;s return address is explicitly provided at checkout</li>
        <li>If a pickup or return address is available it will be displayed during checkout under shipping and pickup options</li>
        <li>Initiate a return ticket through the Happiness Centre and communicate directly with the licensee</li>
        <li>Return policies for in-person purchases are at the sole discretion of the licensee or vendor</li>
      </ul>

      <h2><span className="doc-section-num">9.</span>Customer Responsibilities</h2>
      <p>Customers are responsible for:</p>
      <ul>
        <li>Initiating return requests within the 15-day window</li>
        <li>Submitting accurate information and clear photos during the return process</li>
        <li>Following all return steps as outlined in this policy</li>
        <li>Communicating directly with the licensee through our system in a timely manner</li>
      </ul>
      <p>eGeez is not liable for:</p>
      <ul>
        <li>Lost, stolen, or damaged items once in the customer&rsquo;s possession</li>
        <li>Failed returns resulting from incomplete steps or failure to communicate through the Platform</li>
        <li>Refund delays caused by customer non-compliance with return instructions</li>
      </ul>

      <h2><span className="doc-section-num">10.</span>Fair Use Policy</h2>
      <p>
        eGeez monitors return activity across the Platform. If we identify abnormal or suspicious
        return patterns we reserve the right to:
      </p>
      <ul>
        <li>Contact you to discuss your return activity</li>
        <li>Limit return privileges on your account</li>
        <li>Suspend your account and any associated accounts in cases of return abuse</li>
      </ul>
      <p>
        If you believe your account has been suspended in error please contact our Happiness Centre
        before sending any items back. Email:{" "}
        <a href="mailto:support@egeez.com">support@egeez.com</a>
      </p>

      <h2><span className="doc-section-num">11.</span>Licensee Discretion</h2>
      <p>
        Each licensee on the eGeez Platform maintains the right to determine in its sole and
        absolute discretion whether a return or refund is warranted and the amount of any
        applicable refund. eGeez facilitates this process but the final refund decision rests with
        the licensee in accordance with their policies and applicable law.
      </p>
      <p>
        eGeez reserves the right to refuse orders or returns that are deemed suspicious,
        fraudulent, or in violation of this policy.
      </p>

      <h2><span className="doc-section-num">12.</span>Escalation and Dispute Resolution</h2>
      <p>If your return is not resolved to your satisfaction:</p>
      <ul>
        <li>Request to speak with a <strong>Happiness Agent</strong> through the Happiness Centre</li>
        <li>Our team will review your case and mediate with the licensee on your behalf</li>
        <li>Unresolved disputes may be escalated in accordance with Section 11 of our Terms of Use</li>
      </ul>

      <h2><span className="doc-section-num">13.</span>Contact Us</h2>
      <p>For any questions regarding this policy or your return:</p>
      <p>
        <strong>eGeez Inc.&trade;</strong><br />
        8 The Green, Suite A<br />
        Dover, DE 19901<br />
        United States<br />
        Email: <a href="mailto:support@egeez.com">support@egeez.com</a><br />
        Happiness Centre: Available in-app via Account Settings<br />
        Website: <a href="https://www.egeez.com" target="_blank" rel="noopener noreferrer">www.egeez.com</a>
      </p>

      <hr />
      <p className="text-black/55">
        <em>eGeez Inc.&trade; — All rights reserved. eGeez is a registered trademark of eGeez Inc., registered with the USPTO and in other jurisdictions worldwide.</em>
      </p>
    </LegalLayout>
  );
}
